import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DatabaseService, MotesPaths, RestService, User } from '@motes-sdk';
import { Observable, firstValueFrom, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { getPublishContentTerms, PUBLISH_CONTENT_TERMS_VERSION } from '@app/services/publish-content-terms';
import { getTtsAudioTerms, TTS_AUDIO_TERMS_VERSION } from '@app/services/tts-audio-terms';

export type CreatorTermsFeature = 'ttsAudioOwnership' | 'publishContentResponsibility';

export interface CreatorTermsAcceptance {
    userKey: string;
    userName: string;
    email: string;
    acceptedOn: any;
    termsVersion: string;
    projectKey?: string;
    projectTitle?: string;
    tourKey?: string;
    tourTitle?: string;
    source?: string;
}

export interface CreatorTermsAcceptanceContext {
    projectKey?: string;
    projectTitle?: string;
    tourKey?: string;
    tourTitle?: string;
    source?: string;
}

interface CreatorTermsFeatureConfig {
    version: string;
    getTermsText: () => string;
    dbPath: (userKey: string) => string;
    slackTitle: string;
}

const FEATURE_CONFIG: Record<CreatorTermsFeature, CreatorTermsFeatureConfig> = {
    ttsAudioOwnership: {
        version: TTS_AUDIO_TERMS_VERSION,
        getTermsText: getTtsAudioTerms,
        dbPath: MotesPaths.TTS_AUDIO_OWNERSHIP_ACCEPTANCE_DB_PATH,
        slackTitle: '🎙 TTS audio terms accepted',
    },
    publishContentResponsibility: {
        version: PUBLISH_CONTENT_TERMS_VERSION,
        getTermsText: getPublishContentTerms,
        dbPath: MotesPaths.PUBLISH_CONTENT_ACCEPTANCE_DB_PATH,
        slackTitle: '📢 Publish content terms accepted',
    },
};

@Injectable({
    providedIn: 'root',
})
export class CreatorTermsService {
    private acceptedVersionCache: { [cacheKey: string]: string } = {};

    constructor(
        private db: DatabaseService,
        private restService: RestService,
        private httpClient: HttpClient,
    ) { }

    public getTermsVersion(feature: CreatorTermsFeature): string {
        return FEATURE_CONFIG[feature].version;
    }

    public getTermsText(feature: CreatorTermsFeature): string {
        return FEATURE_CONFIG[feature].getTermsText();
    }

    /** Live acceptance for the current terms version (updates when RTDB changes). */
    public watchHasAccepted(feature: CreatorTermsFeature, userKey: string): Observable<boolean> {
        if(!userKey) return of(false);

        const version = this.getTermsVersion(feature);
        const cacheKey = this.cacheKey(feature, userKey);
        const path = FEATURE_CONFIG[feature].dbPath(userKey);

        return this.db.object<CreatorTermsAcceptance>(path).valueChanges().pipe(
            map((acceptance) => {
                const accepted = Boolean(acceptance?.acceptedOn) && acceptance?.termsVersion === version;
                if(accepted) this.acceptedVersionCache[cacheKey] = version;
                else delete this.acceptedVersionCache[cacheKey];
                return accepted;
            }),
            distinctUntilChanged(),
        );
    }

    public async hasAccepted(feature: CreatorTermsFeature, userKey: string): Promise<boolean> {
        if(!userKey) return false;
        return firstValueFrom(this.watchHasAccepted(feature, userKey));
    }

    /**
     * Persist acceptance if not already stored for the current terms version.
     * Returns true when the user is allowed to proceed.
     */
    public async ensureAccepted(
        feature: CreatorTermsFeature,
        user: User,
        context: CreatorTermsAcceptanceContext = {},
    ): Promise<boolean> {
        if(!user?.key) return false;
        if(await this.hasAccepted(feature, user.key)) return true;
        await this.accept(feature, user, context);
        return true;
    }

    public async accept(
        feature: CreatorTermsFeature,
        user: User,
        context: CreatorTermsAcceptanceContext = {},
    ): Promise<void> {
        if(!user?.key) return;

        const config = FEATURE_CONFIG[feature];
        const path = config.dbPath(user.key);
        const payload: CreatorTermsAcceptance = {
            userKey: user.key,
            userName: user.userName || '',
            email: user.email || '',
            acceptedOn: this.db.serverTime(),
            termsVersion: config.version,
            projectKey: context.projectKey || '',
            projectTitle: context.projectTitle || '',
            tourKey: context.tourKey || '',
            tourTitle: context.tourTitle || '',
            source: context.source || '',
        };

        await this.db.object(path).set(payload);
        this.acceptedVersionCache[this.cacheKey(feature, user.key)] = config.version;

        this.notifyAcceptedOnSlack(feature, payload).catch((err) => {
            console.error(`Failed to send ${feature} terms Slack notification`, err);
        });
    }

    private async notifyAcceptedOnSlack(feature: CreatorTermsFeature, acceptance: CreatorTermsAcceptance): Promise<void> {
        const config = FEATURE_CONFIG[feature];
        const mailBody = [
            `${config.slackTitle} (v${acceptance.termsVersion})`,
            acceptance.source ? `Source: ${acceptance.source}` : null,
            `User: ${acceptance.userName || 'unknown'} (${acceptance.email || 'no email'})`,
            `User key: ${acceptance.userKey}`,
            acceptance.projectTitle || acceptance.projectKey
                ? `Project: ${acceptance.projectTitle || 'untitled'} (${acceptance.projectKey || 'n/a'})`
                : null,
            acceptance.tourTitle || acceptance.tourKey
                ? `Tour: ${acceptance.tourTitle || 'untitled'} (${acceptance.tourKey || 'n/a'})`
                : null,
            `RTDB: ${config.dbPath(acceptance.userKey)}`,
        ].filter(Boolean).join('\n');

        const headers = await this.restService.getAuthOptions();
        await firstValueFrom(this.httpClient.post(
            `${MotesPaths.MOTES_REST}/api/sendAdminSlack`,
            {
                mailBody,
                userKey: acceptance.userKey,
                userName: acceptance.userName || '',
            },
            { headers }
        ));
    }

    private cacheKey(feature: CreatorTermsFeature, userKey: string): string {
        return `${feature}:${userKey}`;
    }
}
